import { zql, type Schema } from "../../zero/schema";
import { type ExpressionBuilder, type Row, type Transaction, QueryRowType } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import {
  accessDenied,
  capitalizeFirst,
  createId,
  formatNumber,
  FormSchemaType,
  must,
  NIL_UUID,
  nSortIndexesBetween,
} from "@corpuscomplete/core/utils";
import { z } from "zod/v4";
import { food, foodMeta, foodServing } from "./food.sql";
import { Unit } from "@corpuscomplete/core/common/unit";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";
import { foodTypes, foodModifiers, servingModifiers } from "./constants";
import { and, eq, exists, getTableColumns, inArray, or } from "drizzle-orm";
import { QueryBuilder } from "drizzle-orm/pg-core";
import { createInsertSchema } from "../../utils/sql";
import { foodCollection } from "../food-collection/food-collection.sql";
import { FoodCollection } from "../food-collection";
import { accessGrant } from "../../auth/grant/grant.sql";
import { groupMember } from "../../auth/group/group.sql";

/**
 * Food domain boundary for permissions, items, and serving conversion helpers.
 *
 * Query and mutator surfaces here define the stable food contract consumed by
 * Zero-backed features.
 *
 * @see `packages/core/docs/orpc.md`
 * @see `packages/core/docs/zero.md`
 */
export namespace Food {
  export function viewableCondition(actor: UserActor) {
    const qb = new QueryBuilder();
    const actorGroupIds = qb
      .select({ groupId: groupMember.groupId })
      .from(groupMember)
      .where(eq(groupMember.userId, actor.userId));

    const publicCollection = qb
      .select({ id: foodCollection.id })
      .from(foodCollection)
      .where(
        and(eq(foodCollection.id, foodMeta.collectionId), eq(foodCollection.visibility, "public")),
      );

    const collectionGrant = qb
      .select({ id: accessGrant.id })
      .from(accessGrant)
      .where(
        and(
          eq(accessGrant.resourceId, foodMeta.collectionId),
          eq(accessGrant.resourceType, "food_collection"),
          eq(accessGrant.resourceScope, "contents"),
          inArray(accessGrant.role, ["editor", "viewer"]),
          or(
            and(eq(accessGrant.granteeType, "user"), eq(accessGrant.granteeId, actor.userId)),
            and(
              eq(accessGrant.granteeType, "group"),
              inArray(accessGrant.granteeId, actorGroupIds),
            ),
          ),
        ),
      );

    const directFoodGrant = qb
      .select({ id: accessGrant.id })
      .from(accessGrant)
      .where(
        and(
          eq(accessGrant.resourceId, foodMeta.id),
          eq(accessGrant.resourceType, "food"),
          eq(accessGrant.resourceScope, "root"),
          eq(accessGrant.role, "viewer"),
          or(
            and(eq(accessGrant.granteeType, "user"), eq(accessGrant.granteeId, actor.userId)),
            and(
              eq(accessGrant.granteeType, "group"),
              inArray(accessGrant.granteeId, actorGroupIds),
            ),
          ),
        ),
      );

    return or(
      and(eq(foodMeta.visibility, "public"), exists(publicCollection)),
      exists(collectionGrant),
      exists(directFoodGrant),
    );
  }

  export function currentVersionJoinCondition() {
    return and(eq(food.id, foodMeta.id), eq(food.version, foodMeta.currentVersion));
  }

  export function infoSelect() {
    return {
      ...getTableColumns(food),
      meta: getTableColumns(foodMeta),
    };
  }

  export function viewable(eb: ExpressionBuilder<"foodMeta", Schema>, actor: UserActor) {
    return eb.or(
      // Public food
      eb.and(
        eb.cmp("visibility", "=", "public"),
        eb.exists("collection", (q) => q.where("visibility", "=", "public"), {
          flip: true,
        }),
      ),
      eb.exists(
        "grantsByCollection",
        (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("food_collection", "contents", ["editor", "viewer"]),
          ),
        {
          flip: true,
        },
      ),
      eb.exists(
        "grants",
        (r) => AccessGrant.matching(r, actor, AccessGrant.criteria("food", "root", ["viewer"])),
        {
          flip: true,
        },
      ),
    );
  }

  export type Meta = Row<Schema["tables"]["foodMeta"]>;

  export type CanViewResult = { ok: true; currentMeta: Meta } | { ok: false };

  export async function canView(
    tx: Transaction,
    actor: UserActor,
    foodId: string,
  ): Promise<boolean> {
    if (tx.location === "client") {
      return true;
    }

    const result = await tx.run(
      zql.foodMeta
        .where("id", "=", foodId)
        .where((e) => viewable(e, actor))
        .one(),
    );
    return !!result;
  }

  export async function canViewByServingId(
    tx: Transaction,
    actor: UserActor,
    foodServingId: string,
  ): Promise<boolean> {
    if (tx.location === "client") {
      return true;
    }

    const serving = await tx.run(zql.foodServing.where("id", "=", foodServingId).one());
    if (!serving) {
      return false;
    }
    return canView(tx, actor, serving.foodId);
  }

  export async function canViewBatch(
    tx: Transaction,
    actor: UserActor,
    foodIds: string[],
  ): Promise<boolean> {
    if (tx.location === "client") {
      return true;
    }

    if (foodIds.length === 0) {
      return true;
    }
    const uniqueFoodIds = [...new Set(foodIds)];
    const results = await tx.run(
      zql.foodMeta.where("id", "IN", uniqueFoodIds).where((e) => viewable(e, actor)),
    );
    return results.length === uniqueFoodIds.length;
  }

  export async function canViewByServingIdBatch(
    tx: Transaction,
    actor: UserActor,
    foodServingIds: string[],
  ): Promise<boolean> {
    if (tx.location === "client") {
      return true;
    }

    if (foodServingIds.length === 0) {
      return true;
    }
    const uniqueFoodServingIds = [...new Set(foodServingIds)];
    const results = await tx.run(zql.foodServing.where("id", "IN", uniqueFoodServingIds));
    return canViewBatch(
      tx,
      actor,
      results.map((serving) => serving.foodId),
    );
  }

  export type CanEditResult<T = {}> =
    | ({ ok: true; currentMeta: Meta; newVersion: number } & T)
    | { ok: false; error: string };

  export async function canEdit(
    tx: Transaction,
    actor: UserActor,
    {
      id,
      currentVersion,
      type,
    }: {
      id: string;
      currentVersion: number;
      type: Type;
    },
    op: { collectionId: string; op: "edit" } | { op: "archive" },
  ): Promise<CanEditResult> {
    const existing = await tx.run(
      zql.foodMeta
        .where("id", "=", id)
        .whereExists("grantsByCollection", (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("food_collection", "contents", ["editor"]),
          ),
        )
        .one(),
    );

    if (!existing) {
      return { ok: false, error: `Food not found. id: "${id}"` };
    }
    if (existing.currentVersion === null || existing.currentVersion !== currentVersion) {
      return {
        ok: false,
        error: `Food version mismatch. id: "${id}", currentVersion: "${currentVersion}", existing.currentVersion: "${existing.currentVersion}"`,
      };
    }
    if (existing.type !== type) {
      return {
        ok: false,
        error: `Food type mismatch. id: "${id}", type: "${type}", existing.type: "${existing.type}"`,
      };
    }
    if (existing.timeArchived) {
      return { ok: false, error: `Food is archived. id: "${id}"` };
    }

    if (op.op === "edit" && op.collectionId !== existing.collectionId) {
      const externalLink = await tx.run(zql.externalFoodIdentity.where("foodId", "=", id).one());
      if (externalLink) {
        throw new Error("Externally linked recipes cannot be moved between collections");
      }
      const canAddToTarget = await Food.canAddToCollection(tx, actor, op.collectionId, type);
      if (!canAddToTarget) {
        accessDenied("You do not have permission to move the recipe to this collection");
      }
    }

    const newVersion = existing.currentVersion + 1;
    return { ok: true, currentMeta: existing, newVersion };
  }

  export async function archive(
    tx: Transaction,
    actor: UserActor,
    { id, currentVersion, type }: { id: string; currentVersion: number; type: Type },
  ) {
    const canEditResult = await canEdit(tx, actor, { id, currentVersion, type }, { op: "archive" });
    if (!canEditResult.ok) {
      accessDenied(canEditResult.error);
    }

    const timeNow = Temporal.Now.instant();
    await tx.mutate.foodMeta.update({
      id,
      timeArchived: timeNow,
      timeUpdated: timeNow,
    });
  }

  export const createBaseSchema = createInsertSchema(food)
    .omit({ version: true })
    .extend({
      meta: createInsertSchema(foodMeta).omit({ currentVersion: true, id: true, type: true }),
    });
  export type CreateBase = z.infer<typeof createBaseSchema>;

  export const baseSchema = () =>
    createInsertSchema(food)
      .omit({ version: true, id: true })
      .extend({ modifiers: modifierSchema.array() });
  export type BaseInput = z.infer<ReturnType<typeof baseSchema>>;
  export const formBaseSchema = (_type: FormSchemaType) => baseSchema();
  export type FormBase = z.infer<ReturnType<typeof formBaseSchema>>;
  export const formBaseDefault = (): FormBase => ({
    name: "",
    modifiers: [],
    iconSlugs: [],
  });

  export const categoryAssociationsSchema = () =>
    z
      .object({
        categoryId: z.uuid(),
        // Only "isA" associations are created manually, all others are derived
        type: z.enum(["isA"]),
      })
      .array();
  export type CategoryAssociationInput = z.infer<ReturnType<typeof categoryAssociationsSchema>>;
  export const formCategoryAssociationsSchema = (_type: FormSchemaType) =>
    categoryAssociationsSchema();
  export type FormCategoryAssociations = z.infer<ReturnType<typeof formCategoryAssociationsSchema>>;
  export const formCategoryAssociationsDefault = (): FormCategoryAssociations => [];
  export const toFormCategoryAssociations = (
    categoryAssociations: readonly Omit<CategoryAssociation, "foodId" | "foodVersion">[],
  ): FormCategoryAssociations => {
    const result: FormCategoryAssociations = [];
    for (const association of categoryAssociations) {
      if (association.type === "isA") {
        result.push({
          categoryId: association.categoryId,
          type: association.type,
        });
      }
    }
    return result;
  };

  export const metaSchema = () =>
    createInsertSchema(foodMeta).omit({ currentVersion: true, id: true, type: true });
  export type MetaInput = z.infer<ReturnType<typeof metaSchema>>;
  export const formMetaSchema = (_type: FormSchemaType) => metaSchema();
  export type FormMeta = z.infer<ReturnType<typeof formMetaSchema>>;
  export const formMetaDefault = (collectionId: string | undefined): FormMeta => ({
    visibility: "private",
    collectionId: collectionId ?? NIL_UUID,
    discoverability: "listed",
  });

  export async function canAddToCollection(
    tx: Transaction,
    actor: UserActor,
    collectionId: string,
    foodType: Type,
  ): Promise<boolean> {
    const collection = await tx.run(
      zql.foodCollection
        .where("id", "=", collectionId)
        .whereExists("grants", (r) =>
          AccessGrant.matching(
            r,
            actor,
            AccessGrant.criteria("food_collection", "contents", ["editor"]),
          ),
        )
        .one(),
    );
    return !!collection && FoodCollection.allowsFoodType(collection, foodType);
  }

  export type Type = (typeof foodTypes)[number];
  export const types = foodTypes;

  export const modifierSchema = z.enum(foodModifiers);
  export type Modifier = (typeof foodModifiers)[number];

  export const servingModifierSchema = z.enum(servingModifiers);
  export type ServingModifier = (typeof servingModifiers)[number];

  export function isModifier(modifier: string): modifier is Modifier {
    return modifierSchema.safeParse(modifier).success;
  }

  export function isServingModifier(modifier: string): modifier is ServingModifier {
    return servingModifierSchema.safeParse(modifier).success;
  }

  export function isValidModifier(modifier: string): modifier is Modifier | ServingModifier {
    return isModifier(modifier) || isServingModifier(modifier);
  }

  export function formatModifiers(modifiers: readonly (Modifier | ServingModifier)[]) {
    return modifiers.map(capitalizeFirst).join(", ");
  }

  export const byServingId = defineQuery(
    z.object({ foodServingId: z.string() }),
    ({ ctx, args: { foodServingId } }) =>
      zql.food
        .whereExists("servings", (q) => q.where("id", "=", foodServingId))
        .whereExists("meta", (q) => q.where((e) => viewable(e, ctx.actor)))
        .related("servings", (q) => q.orderBy("sortIndex", "asc"))
        .related("meta")
        .one(),
  );
  export type RawInfo = QueryRowType<typeof byServingId>;

  export function servingsByIds(foodServingIds: readonly string[]) {
    return zql.foodServing
      .where("id", "IN", [...foodServingIds])
      .related("food", (q) => q.related("meta"));
  }

  export type Info = Row<Schema["tables"]["food"]> & {
    meta: Meta;
  };
  export type MaybeInfo = Row<Schema["tables"]["food"]> & {
    meta: Meta | undefined;
  };
  export type Serving = Row<Schema["tables"]["foodServing"]>;

  export const itemInputSchema = z.object({
    amount: z.number().positive().max(999_999),
    foodServingId: z.uuid(),
  });
  export type ItemInput = z.infer<typeof itemInputSchema>;

  export type AsItem<T extends MaybeItem> = Omit<T, "serving"> & {
    food: Info;
    serving: Serving;
  };
  export type Item = {
    amount: number;
    food: Info;
    serving: Serving;
  };
  export type MaybeItem = {
    amount: number;
    serving:
      | (Serving & {
          food: MaybeInfo | undefined;
        })
      | undefined;
  };
  export const asItem = <T extends MaybeItem>(item: T): AsItem<T> | null => {
    if (!item.serving?.food?.meta) {
      return null;
    }
    return {
      ...item,
      amount: item.amount,
      food: {
        ...item.serving.food,
        meta: item.serving.food.meta,
      },
      serving: item.serving,
    };
  };
  export const asItems = <T extends MaybeItem>(maybeItems: T[] | readonly T[]): AsItem<T>[] => {
    const items: AsItem<T>[] = [];
    for (const maybeItem of maybeItems) {
      const item = asItem(maybeItem);
      if (item) {
        items.push(item);
      }
    }
    return items;
  };

  export function itemsEqual(
    left: readonly ItemInput[] | readonly Item[],
    right: readonly ItemInput[] | readonly Item[],
  ): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const counts = new Map<string, number>();
    for (const item of left) {
      const key = "foodServingId" in item ? item.foodServingId : item.serving.id;
      const amountKey = `${key}:${item.amount}`;
      counts.set(amountKey, (counts.get(amountKey) ?? 0) + 1);
    }

    for (const item of right) {
      const key = "foodServingId" in item ? item.foodServingId : item.serving.id;
      const amountKey = `${key}:${item.amount}`;
      const count = counts.get(amountKey);
      if (!count) {
        return false;
      }
      if (count === 1) {
        counts.delete(amountKey);
      } else {
        counts.set(amountKey, count - 1);
      }
    }

    return counts.size === 0;
  }

  export function inputToItem(input: ItemInput, raw: RawInfo): Item | undefined {
    const serving = raw?.servings.find((serving) => serving.id === input.foodServingId);
    if (!serving || !raw?.meta) {
      return undefined;
    }
    const { meta, servings: _servings, ...food } = raw;
    return {
      amount: input.amount,
      serving: {
        ...serving,
      },
      food: {
        ...food,
        meta,
      },
    };
  }

  export function formatItem({ food, serving, amount }: Item): {
    title: string;
    subtitle: string;
  } {
    return {
      title: food.name,
      subtitle: `${formatNumber(amount)} ${formatServing(serving)}`,
    };
  }

  export function formatServing(serving: Pick<Item["serving"], "name" | "modifiers">): string {
    const modifiers = serving.modifiers;
    if (modifiers.length > 0) {
      return `${serving.name}, ${modifiers.join(", ")}`;
    }
    return serving.name;
  }

  export type CategoryAssociation = Row<Schema["tables"]["foodCategoryAssociation"]>;

  export const ItemDescription = z.object({
    name: z
      .string()
      .min(1)
      .max(255)
      .describe("The name of the food item, e.g. 'Eggs', 'Chicken', 'Spinach'"),
    amount: z
      .number()
      .positive()
      .max(999_999)
      .describe("The amount of the food item, e.g. 1, 2.5, 0.75"),
    unit: z
      .string()
      .min(1)
      .max(100)
      .describe("The serving size of the food item, e.g. 'cups', 'oz', 'grams'."),
    modifiers: z
      .array(z.string().max(100))
      .optional()
      .describe(
        "Preparation modifiers for the serving, e.g. ['chopped'], ['diced', 'drained']. Should not include cooking preparations.",
      ),
  });
  export type ItemDescription = z.infer<typeof ItemDescription>;
  export function itemDescriptionToString(foodItemDescription: ItemDescription) {
    const modifierStr = foodItemDescription.modifiers?.join(", ");
    if (modifierStr) {
      return `${foodItemDescription.unit}, ${modifierStr} of ${foodItemDescription.name}`;
    }
    return `${foodItemDescription.unit} of ${foodItemDescription.name}`;
  }

  export const servingSchema = createInsertSchema(foodServing)
    .omit({
      foodId: true,
      foodVersion: true,
      nutrients: true,
      massQuantity: true,
      volumeQuantity: true,
      massUnitKey: true,
      volumeUnitKey: true,
      sortIndex: true,
    })
    .extend({
      modifiers: servingModifierSchema.array(),
      defaultAmount: z.number().positive(),
      mass: z
        .object({
          unitKey: Unit.keySchema(["mass"]),
          quantity: z.number().positive(),
        })
        .nullable(),
      volume: z
        .object({
          unitKey: Unit.keySchema(["volume"]),
          quantity: z.number().positive(),
        })
        .nullable(),
    });

  export type ServingInput = z.infer<typeof servingSchema> & {
    nutrients: Nutrient.Amounts;
  };

  export type ServingsInput = (ServingInput & { sortIndex: string })[];

  export function servingInputToRow(serving: ServingInput & { sortIndex?: string }): Omit<
    Row<Schema["tables"]["foodServing"]>,
    "foodId" | "foodVersion" | "nutrients"
  > & {
    nutrients: Nutrient.NormalizedAmounts;
  } {
    return {
      id: serving.id,
      sortIndex: serving.sortIndex ?? "",
      name: serving.name,
      modifiers: serving.modifiers ?? [],
      defaultAmount: serving.defaultAmount,
      massQuantity: serving.mass?.quantity ?? null,
      massUnitKey: serving.mass?.unitKey ?? null,
      volumeQuantity: serving.volume?.quantity ?? null,
      volumeUnitKey: serving.volume?.unitKey ?? null,
      nutrients: Nutrient.normalizeAmounts(serving.nutrients),
    };
  }

  const volumeUnitKeysForServingConversion: Unit.Key<"volume">[] = [
    "mL",
    "cup_us",
    "fl_oz_us",
    "tbsp_us",
    "tsp_us",
    // Including these so that I can rely on the excludeImperial flag
    "cup_imp",
    "fl_oz_imp",
    "tbsp_imp",
    "tsp_imp",
  ];

  const massUnitKeysForServingConversion: Unit.Key<"mass">[] = ["g", "oz", "lb"];

  const massUnitSortPriority: Record<Unit.Key<"mass">, number> = {
    g: 0,
    oz: 1,
    lb: 2,
    kg: 3,
    mg: 4,
    mcg: 5,
    st: 6,
  };

  const volumeUnitSortPriority: Record<Unit.Key<"volume">, number> = {
    mL: 0,
    fl_oz_us: 1,
    cup_us: 2,
    tbsp_us: 3,
    tsp_us: 4,
    L: 5,
    fl_oz_imp: 6,
    cup_imp: 7,
    tbsp_imp: 8,
    tsp_imp: 9,
    pt_us: 10,
    pt_imp: 11,
    mcL: 12,
  };

  export function sortServings(servings: ServingInput[], units: Unit.Info[]): ServingsInput {
    const unitKeyMap = new Map(units.map((u) => [u.key, u]));
    const unitPriority = { ...massUnitSortPriority, ...volumeUnitSortPriority } as Record<
      string,
      number
    >;

    const sorted = servings
      .map((serving, inputIndex) => ({
        serving,
        inputIndex,
        unit: getUnitForServing(serving, unitKeyMap),
      }))
      .sort((a, b) => {
        const aType = a.unit?.type;
        const bType = b.unit?.type;

        if (!aType && !bType) return a.inputIndex - b.inputIndex;
        if (!aType) return -1;
        if (!bType) return 1;
        if (aType !== bType) return aType === "mass" ? -1 : 1;

        const priorityDiff =
          (unitPriority[must(a.unit).key] ?? 999) - (unitPriority[must(b.unit).key] ?? 999);
        if (priorityDiff !== 0) return priorityDiff;

        const aModifier = a.serving.modifiers?.[0] ?? null;
        const bModifier = b.serving.modifiers?.[0] ?? null;
        if (aModifier === null && bModifier !== null) return -1;
        if (aModifier !== null && bModifier === null) return 1;
        return aModifier && bModifier ? aModifier.localeCompare(bModifier) : 0;
      });

    const sortIndexes = nSortIndexesBetween(null, null, sorted.length);
    return sorted.map((item, i) => ({ ...item.serving, sortIndex: must(sortIndexes[i]) }));
  }

  export function addUnitConversionServings(
    servings: ServingInput[],
    units: Unit.Info[],
    excludeImperial: boolean = true,
  ): ServingInput[] {
    const unitKeyMap = new Map(units.map((u) => [u.key, u]));
    const massUnits = units.filter(
      (u) =>
        u.type === "mass" && massUnitKeysForServingConversion.includes(u.key as Unit.Key<"mass">),
    );
    const volumeUnits = units.filter((u) => {
      if (excludeImperial && u.key.endsWith("_imp")) {
        return false;
      }
      return (
        u.type === "volume" &&
        volumeUnitKeysForServingConversion.includes(u.key as Unit.Key<"volume">)
      );
    });

    type UnitModifierType = "invalid" | "explicit" | "implicit";

    const resultServings: ServingInput[] = [];
    const massUnitServings: {
      serving: ServingInput;
      representativeUnitKey: Unit.Key<"mass"> | undefined;
      massQuantity: number;
      massUnitKey: Unit.Key<"mass">;
      energyPerG: number;
      unitModifier: ServingModifier | null;
      unitModifierType: UnitModifierType;
    }[] = [];
    const volumeUnitServings: {
      serving: ServingInput;
      representativeUnitKey: Unit.Key<"volume"> | undefined;
      volumeQuantity: number;
      volumeUnitKey: Unit.Key<"volume">;
      energyPerML: number;
      unitModifier: ServingModifier | null;
      unitModifierType: UnitModifierType;
    }[] = [];

    for (const serving of servings) {
      const energy = must(serving.nutrients.energy, "Energy is required");
      const representativeUnit = getUnitForServing(serving, unitKeyMap);
      const modifiers = serving.modifiers ?? [];
      const cleanedUnitModifier = modifiers[0]?.trim().split(",")[0]?.split(" ")[0] ?? null;

      if (!representativeUnit) {
        // Always add non-unit servings to final results
        resultServings.push(serving);
      }

      if (serving.mass) {
        let unitModifier: ServingModifier | null;
        let unitModifierType: UnitModifierType;
        if (!representativeUnit || representativeUnit.key !== serving.mass.unitKey) {
          unitModifier = null;
          unitModifierType = "implicit";
        } else {
          if (cleanedUnitModifier == null) {
            unitModifierType = "explicit";
            unitModifier = null;
          } else if (isValidModifier(cleanedUnitModifier)) {
            unitModifierType = "explicit";
            unitModifier = cleanedUnitModifier as ServingModifier;
          } else {
            unitModifierType = "invalid";
            unitModifier = null;
          }
        }

        massUnitServings.push({
          serving,
          representativeUnitKey: representativeUnit?.key as Unit.Key<"mass"> | undefined,
          massQuantity: serving.mass.quantity,
          massUnitKey: serving.mass.unitKey,
          energyPerG: Unit.convert(
            { quantity: energy / serving.mass.quantity, unitKey: "g" },
            serving.mass.unitKey,
          ).quantity,
          unitModifier,
          unitModifierType,
        });
      }

      if (serving.volume) {
        let unitModifier: ServingModifier | null;
        let unitModifierType: UnitModifierType;
        if (!representativeUnit || representativeUnit.key !== serving.volume.unitKey) {
          unitModifier = null;
          unitModifierType = "implicit";
        } else {
          if (cleanedUnitModifier == null) {
            unitModifierType = "explicit";
            unitModifier = null;
          } else if (isValidModifier(cleanedUnitModifier)) {
            unitModifierType = "explicit";
            unitModifier = cleanedUnitModifier as ServingModifier;
          } else {
            unitModifierType = "invalid";
            unitModifier = null;
          }
        }

        volumeUnitServings.push({
          serving,
          representativeUnitKey: representativeUnit?.key as Unit.Key<"volume"> | undefined,
          volumeQuantity: serving.volume.quantity,
          volumeUnitKey: serving.volume.unitKey,
          energyPerML: Unit.convert(
            { quantity: energy / serving.volume.quantity, unitKey: "mL" },
            serving.volume.unitKey,
          ).quantity,
          unitModifier,
          unitModifierType,
        });
      }
    }

    // Mass unit processing
    {
      let anyValidModifier = false;
      let invalidModifierCount = 0;
      const energyPerGsGroups: {
        energyPerG: number;
        servingIds: string[];
        modifiers: { modifier: ServingModifier | null; type: UnitModifierType }[];
      }[] = [];
      for (const { serving, unitModifier, energyPerG, unitModifierType } of massUnitServings) {
        if (unitModifierType === "invalid") {
          invalidModifierCount++;
          continue;
        }

        anyValidModifier = true;
        const matchingGroup = energyPerGsGroups.find((g) =>
          isVeryCloseTo(g.energyPerG, energyPerG),
        );
        if (matchingGroup) {
          matchingGroup.servingIds.push(serving.id);
          matchingGroup.modifiers.push({
            modifier: unitModifier,
            type: unitModifierType,
          });
        } else {
          energyPerGsGroups.push({
            energyPerG,
            servingIds: [serving.id],
            modifiers: [{ modifier: unitModifier, type: unitModifierType }],
          });
        }
      }

      const skipInvalidModifiers = anyValidModifier || invalidModifierCount !== 1;

      const servingIdsToChosenModifier = new Map<string, ServingModifier | null>();
      for (const { servingIds, modifiers } of energyPerGsGroups) {
        if (modifiers.length <= 1) {
          continue;
        }

        // Explicit, null is highest priority
        // Then explicit, non-null
        // Finally implicit (which should always just be null)
        let chosenModifier: ServingModifier | null = null;
        for (const { modifier, type } of modifiers) {
          if (type === "explicit") {
            if (modifier !== null && chosenModifier === null) {
              chosenModifier = modifier;
            } else if (modifier === null) {
              chosenModifier = null;
              break;
            }
          }
        }

        for (const servingId of servingIds) {
          servingIdsToChosenModifier.set(servingId, chosenModifier);
        }
      }

      const seenUnitModifiers = new Set<ServingModifier | null>();
      for (const {
        serving,
        representativeUnitKey,
        massQuantity,
        massUnitKey,
        unitModifier,
        unitModifierType,
      } of massUnitServings) {
        if (unitModifierType === "invalid" && skipInvalidModifiers) {
          continue;
        }
        const chosenModifier = servingIdsToChosenModifier.get(serving.id);
        if (chosenModifier !== undefined && chosenModifier !== unitModifier) {
          continue;
        }
        if (seenUnitModifiers.has(unitModifier)) {
          continue;
        }
        seenUnitModifiers.add(unitModifier);

        for (const toUnit of massUnits) {
          const conversionFactor = Unit.convert(
            {
              quantity: 1 / massQuantity,
              unitKey: toUnit.key,
            },
            massUnitKey,
          ).quantity;

          resultServings.push({
            id: representativeUnitKey === toUnit.key ? serving.id : createId(),
            name: toUnit.defaultName,
            modifiers: unitModifier ? [unitModifier] : [],
            defaultAmount: 1,
            mass: {
              unitKey: toUnit.key as Unit.Key<"mass">,
              quantity: 1,
            },
            volume: null,
            nutrients: Nutrient.scaleAmounts(serving.nutrients, conversionFactor),
          });
        }
      }
    }

    // Volume unit processing
    {
      let anyValidModifier = false;
      let invalidModifierCount = 0;
      const energyPerMLsGroups: {
        energyPerML: number;
        servingIds: string[];
        modifiers: { modifier: ServingModifier | null; type: UnitModifierType }[];
      }[] = [];
      for (const { serving, unitModifier, energyPerML, unitModifierType } of volumeUnitServings) {
        if (unitModifierType === "invalid") {
          invalidModifierCount++;
          continue;
        }

        anyValidModifier = true;
        const matchingGroup = energyPerMLsGroups.find((g) =>
          isVeryCloseTo(g.energyPerML, energyPerML),
        );
        if (matchingGroup) {
          matchingGroup.servingIds.push(serving.id);
          matchingGroup.modifiers.push({
            modifier: unitModifier,
            type: unitModifierType,
          });
        } else {
          energyPerMLsGroups.push({
            energyPerML,
            servingIds: [serving.id],
            modifiers: [{ modifier: unitModifier, type: unitModifierType }],
          });
        }
      }

      const skipInvalidModifiers = anyValidModifier || invalidModifierCount !== 1;

      const servingIdsToChosenModifier = new Map<string, ServingModifier | null>();
      for (const { servingIds, modifiers } of energyPerMLsGroups) {
        if (modifiers.length <= 1) {
          continue;
        }

        // Explicit, null is highest priority
        // Then explicit, non-null
        // Finally implicit (which should always just be null)
        let chosenModifier: ServingModifier | null = null;
        for (const { modifier, type } of modifiers) {
          if (type === "explicit") {
            if (modifier !== null && chosenModifier === null) {
              chosenModifier = modifier;
            } else if (modifier === null) {
              chosenModifier = null;
              break;
            }
          }
        }

        for (const servingId of servingIds) {
          servingIdsToChosenModifier.set(servingId, chosenModifier);
        }
      }

      const seenUnitModifiers = new Set<ServingModifier | null>();
      for (const {
        serving,
        representativeUnitKey,
        volumeQuantity,
        volumeUnitKey,
        unitModifier,
        unitModifierType,
      } of volumeUnitServings) {
        if (unitModifierType === "invalid" && skipInvalidModifiers) {
          continue;
        }
        const chosenModifier = servingIdsToChosenModifier.get(serving.id);
        if (chosenModifier !== undefined && chosenModifier !== unitModifier) {
          continue;
        }
        if (seenUnitModifiers.has(unitModifier)) {
          continue;
        }
        seenUnitModifiers.add(unitModifier);

        for (const toUnit of volumeUnits) {
          const conversionFactor = Unit.convert(
            {
              quantity: 1 / volumeQuantity,
              unitKey: toUnit.key,
            },
            volumeUnitKey,
          ).quantity;

          // Fill in mass quantity and unit key on volume servings since this may be useful
          let mass: Unit.Quantity<"mass"> | undefined;
          if (serving.mass) {
            mass = Unit.convert(
              {
                quantity: serving.mass.quantity * conversionFactor,
                unitKey: serving.mass.unitKey,
              },
              "g",
            );
          }

          resultServings.push({
            id: representativeUnitKey === toUnit.key ? serving.id : createId(),
            name: toUnit.defaultName,
            modifiers: unitModifier ? [unitModifier] : [],
            defaultAmount: 1,
            volume: {
              unitKey: toUnit.key as Unit.Key<"volume">,
              quantity: 1,
            },
            mass: mass
              ? {
                  unitKey: mass.unitKey,
                  quantity: mass.quantity,
                }
              : null,
            nutrients: Nutrient.scaleAmounts(serving.nutrients, conversionFactor),
          });
        }
      }
    }

    return resultServings;
  }

  export function assertNoDuplicateUnitServings(
    servings: ServingInput[],
    units: Unit.Info[],
  ): void {
    const seen = new Map<
      string,
      {
        serving: ServingInput;
        identity: UnitServingIdentity;
      }
    >();
    const collisions: string[] = [];

    for (const serving of servings) {
      const identity = getUnitServingIdentity(serving, units);
      if (!identity) {
        continue;
      }

      const duplicateKey = `${identity.unit.key}||${identity.normalizedModifiers.join("|")}`;
      const previous = seen.get(duplicateKey);
      if (!previous) {
        seen.set(duplicateKey, {
          serving,
          identity,
        });
        continue;
      }

      collisions.push(
        formatDuplicateUnitServingCollision(
          previous.serving,
          serving,
          previous.identity.unit,
          previous.identity.normalizedModifiers,
        ),
      );
    }

    if (collisions.length > 0) {
      throw new Error(`Duplicate unit servings are not allowed. ${collisions.join("; ")}`);
    }
  }

  export type UnitServingIdentity = {
    unit: Unit.Info;
    normalizedModifiers: string[];
  };

  export type FractionDenominator = 2 | 3 | 4;

  export type PrecisionPolicyByUnitKey = Partial<Record<Unit.Key, readonly FractionDenominator[]>>;

  export type ServingPrecisionPolicy =
    | {
        mode: "integer_only";
      }
    | {
        mode: "fractional";
        unitKey: Unit.Key;
        allowedDenominators: readonly FractionDenominator[];
      };

  const servingPrecisionPolicyByUnitKey: PrecisionPolicyByUnitKey = {
    cup_us: [4, 3],
    cup_imp: [4, 3],
    tbsp_us: [2, 4],
    tbsp_imp: [2, 4],
    tsp_us: [2, 4],
    tsp_imp: [2, 4],
    fl_oz_us: [2],
    fl_oz_imp: [2],
    oz: [2],
    lb: [2],
  };

  export function getServingPrecisionPolicy(
    serving: Omit<ServingInput, "nutrients">,
    units: readonly Unit.Info[],
  ): ServingPrecisionPolicy {
    const identity = getUnitServingIdentity(serving, units);
    if (!identity) {
      return { mode: "integer_only" };
    }

    const allowedDenominators = servingPrecisionPolicyByUnitKey[identity.unit.key];
    if (!allowedDenominators) {
      return { mode: "integer_only" };
    }

    return {
      mode: "fractional",
      unitKey: identity.unit.key,
      allowedDenominators,
    };
  }

  export function getUnitServingIdentity(
    serving: Omit<ServingInput, "nutrients">,
    units: readonly Unit.Info[],
  ): UnitServingIdentity | undefined {
    const unitKeyMap = new Map(units.map((unit) => [unit.key, unit]));
    const unit = getUnitIdentityForServing(serving, unitKeyMap);
    if (!unit) {
      return undefined;
    }

    return {
      unit,
      normalizedModifiers: normalizeServingModifiersForIdentity(serving.modifiers),
    };
  }

  function getUnitForServing(
    serving: ServingInput,
    unitKeyMap: Map<Unit.Key, Unit.Info>,
  ): Unit.Info | undefined {
    const massUnit = serving.mass && unitKeyMap.get(serving.mass.unitKey);
    const volumeUnit = serving.volume && unitKeyMap.get(serving.volume.unitKey);

    if (massUnit && serving.mass?.quantity === 1 && nameMatchesUnit(serving.name, massUnit)) {
      return massUnit;
    } else if (
      volumeUnit &&
      serving.volume?.quantity === 1 &&
      nameMatchesUnit(serving.name, volumeUnit)
    ) {
      return volumeUnit;
    } else {
      return undefined;
    }
  }

  function getUnitIdentityForServing(
    serving: Omit<ServingInput, "nutrients">,
    unitKeyMap: Map<Unit.Key, Unit.Info>,
  ): Unit.Info | undefined {
    const massUnit = serving.mass && unitKeyMap.get(serving.mass.unitKey);
    if (massUnit?.type === "mass" && servingNameMatchesUnitFamily(serving.name, massUnit)) {
      return massUnit;
    }

    const volumeUnit = serving.volume && unitKeyMap.get(serving.volume.unitKey);
    if (volumeUnit?.type === "volume" && servingNameMatchesUnitFamily(serving.name, volumeUnit)) {
      return volumeUnit;
    }

    return undefined;
  }

  function nameMatchesUnit(name: string, unit: Unit.Info): boolean {
    return name === unit.defaultName || unit.defaultAliases.includes(name);
  }

  function servingNameMatchesUnitFamily(name: string, unit: Unit.Info): boolean {
    if (nameMatchesUnit(name, unit)) {
      return true;
    }

    const withoutAmount = stripLeadingAmountPrefix(name);
    return withoutAmount !== null && nameMatchesUnit(withoutAmount, unit);
  }

  function stripLeadingAmountPrefix(name: string): string | null {
    const match = name.match(/^\s*[\d./]+(?:\s+[\d./]+)*\s+(.+?)\s*$/);
    return match?.[1] ?? null;
  }

  function normalizeServingModifiersForIdentity(
    modifiers: readonly string[] | undefined,
  ): string[] {
    if (!modifiers || modifiers.length === 0) {
      return [];
    }

    return [...new Set(modifiers.map(normalizeServingModifierForIdentity).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );
  }

  function normalizeServingModifierForIdentity(modifier: string): string {
    return modifier.trim().toLowerCase().replace(/\s+/g, " ");
  }

  function formatDuplicateUnitServingCollision(
    first: ServingInput,
    second: ServingInput,
    unit: Unit.Info,
    normalizedModifiers: string[],
  ): string {
    const modifierText = normalizedModifiers.length > 0 ? normalizedModifiers.join(", ") : "none";
    return [
      `resolved unit ${unit.key}`,
      `modifiers ${modifierText}`,
      `servings ${formatServingForDuplicateError(first)} and ${formatServingForDuplicateError(second)}`,
    ].join(", ");
  }

  function formatServingForDuplicateError(serving: ServingInput): string {
    return `"${serving.name}" (id: ${serving.id}, defaultAmount: ${serving.defaultAmount})`;
  }

  function isVeryCloseTo(a: number, b: number): boolean {
    // Returns true if a and b are within 0.1 percent of each other
    if (a === b) return true;
    const diff = Math.abs(a - b);
    const max = Math.max(Math.abs(a), Math.abs(b));
    if (max === 0) return false;
    return diff / max <= 0.001;
  }
}
